import React from 'react'
import { Group, Image as KonvaImage, Rect } from 'react-konva'
import type { Coordinate, UnitData } from '../types'
import { BOUNDARY_GAP, CELL_GAP, CELL_SIZE, COLORS, MAP_OFFSET_X, MAP_OFFSET_Y, MAP_SIZE } from '../constants'
import { Unit } from './Unit'
import type { ImageMap } from '../hooks/useImageLoader'

interface GridProps {
  grid: UnitData[][]
  images?: ImageMap | null
  onCellMouseDown?: (coord: Coordinate) => void
  onCellMouseEnter?: (coord: Coordinate) => void
  onCellMouseUp?: (coord: Coordinate) => void
}

/** col, row를 픽셀 좌표로 변환 */
export function cellToPixel(col: number, row: number) {
  const boundaryX = col >= 5 ? BOUNDARY_GAP : 0
  const boundaryY = row >= 5 ? BOUNDARY_GAP : 0
  return {
    x: MAP_OFFSET_X + col * (CELL_SIZE + CELL_GAP) + boundaryX,
    y: MAP_OFFSET_Y + row * (CELL_SIZE + CELL_GAP) + boundaryY,
  }
}

export const Grid: React.FC<GridProps> = ({ grid, images, onCellMouseDown, onCellMouseEnter, onCellMouseUp }) => {
  // Grid background panels (2x2 checkerboard with boundary)
  // 캐릭터 패널 이미지 사용, fallback으로 색상
  const pannel1Img = images?.pannel1 ?? null
  const pannel2Img = images?.pannel2 ?? null
  const panels: React.ReactNode[] = []
  const halfSize = MAP_SIZE / 2
  const panelWidth = halfSize * (CELL_SIZE + CELL_GAP) - CELL_GAP
  const panelHeight = halfSize * (CELL_SIZE + CELL_GAP) - CELL_GAP

  for (let px = 0; px < 2; px++) {
    for (let py = 0; py < 2; py++) {
      const { x, y } = cellToPixel(px * halfSize, py * halfSize)
      const isAlt = (px + py) % 2 === 1
      const pannelImg = isAlt ? pannel2Img : pannel1Img

      if (pannelImg) {
        panels.push(
          <KonvaImage
            key={`panel-${px}-${py}`}
            x={x - 2}
            y={y - 2}
            width={panelWidth + 4}
            height={panelHeight + 4}
            image={pannelImg}
            listening={false}
          />,
        )
      } else {
        panels.push(
          <Rect
            key={`panel-${px}-${py}`}
            x={x - 2}
            y={y - 2}
            width={panelWidth + 4}
            height={panelHeight + 4}
            fill={isAlt ? COLORS.panelB : COLORS.panelA}
            cornerRadius={4}
          />,
        )
      }
    }
  }

  return (
    <Group>
      {panels}
      {grid.map((col, ci) =>
        col.map((unit, ri) => {
          const { x, y } = cellToPixel(ci, ri)
          return (
            <Unit
              key={`map-${ci}-${ri}`}
              x={x}
              y={y}
              state={unit.state}
              images={images}
              onMouseDown={() => onCellMouseDown?.({ col: ci, row: ri })}
              onMouseEnter={() => onCellMouseEnter?.({ col: ci, row: ri })}
              onMouseUp={() => onCellMouseUp?.({ col: ci, row: ri })}
            />
          )
        }),
      )}
    </Group>
  )
}
